import { X, Clock, CheckCircle2, Ban, ReceiptText, CreditCard, Banknote, Smartphone, StickyNote } from "lucide-react";
import { fmtEUR } from "../lib/format";

const STATUS_META = {
  nieuw: { label: "Nieuw", color: "#F4C400" },
  bezig: { label: "Bezig", color: "#FF8A00" },
  klaar: { label: "Klaar", color: "#0E8F2A" },
  afgerond: { label: "Afgerond", color: "#0E8F2A" },
  geannuleerd: { label: "Geannuleerd", color: "#999" },
};

const METHOD_ICON = {
  contant: Banknote,
  sumup: Smartphone,
  "sumup-solo": Smartphone,
  stripe: CreditCard,
  pin: CreditCard,
};

function dt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export default function OrderDetailModal({ order, onClose }) {
  if (!order) return null;
  const status = STATUS_META[order.status] || { label: order.status, color: "#666" };
  const subtotal = order.items.reduce((s, i) => s + i.prijs * i.aantal, 0);
  const MethodIcon = METHOD_ICON[order.betaalmethode] || ReceiptText;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 animate-fade-in"
      style={{ background: "rgba(0,0,0,0.7)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="order-detail-modal"
      role="dialog"
      aria-label={`Order ${order.ordernummer} details`}
    >
      <div
        className="bg-white rounded-[2rem] max-w-lg w-full shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        style={{ borderTop: `14px solid ${order.team_kleur || "#0E8F2A"}` }}
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b-2 border-[#ecebe6] flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="font-display font-extrabold text-3xl px-4 py-2 rounded-2xl text-white leading-none flex-shrink-0"
              style={{ background: order.team_kleur || "#0E8F2A" }}
              data-testid="order-detail-nummer"
            >
              {order.ordernummer}
            </div>
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-[0.3em] text-[var(--ink-soft)] font-bold truncate">
                {order.team_naam}
              </div>
              <div className="inline-flex items-center gap-1.5 mt-1">
                <span
                  className="text-[10px] uppercase tracking-widest font-extrabold px-2 py-1 rounded-full text-white"
                  style={{ background: status.color }}
                  data-testid="order-detail-status"
                >
                  {status.label}
                </span>
                <span className="text-[10px] uppercase tracking-widest font-extrabold px-2 py-1 rounded-full bg-[var(--neutral)] inline-flex items-center gap-1">
                  <MethodIcon className="w-3 h-3" />
                  {order.betaalmethode}
                </span>
                {order.betaal_status === "wachten" && (
                  <span className="text-[10px] uppercase tracking-widest font-extrabold px-2 py-1 rounded-full bg-[#FFF4D6] text-[#8A6500]">
                    Wacht op betaling
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="btn-press w-11 h-11 rounded-full bg-[var(--neutral)] flex items-center justify-center flex-shrink-0"
            data-testid="order-detail-close"
            aria-label="Sluiten"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Items */}
          <section>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--ink-soft)] font-bold mb-2">
              Items ({order.items.length})
            </div>
            <div className="rounded-2xl bg-[var(--neutral)] divide-y divide-white/60" data-testid="order-detail-items">
              {order.items.map((it, idx) => (
                <div key={idx} className="flex items-start gap-3 px-4 py-3" data-testid={`order-detail-item-${idx}`}>
                  <span className="font-display font-extrabold text-lg tabular-nums w-8 flex-shrink-0">
                    {it.aantal}×
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{it.naam}</div>
                    <div className="text-xs text-[var(--ink-soft)] tabular-nums">
                      {fmtEUR(it.prijs)} per stuk
                    </div>
                  </div>
                  <div className="font-display font-extrabold tabular-nums">
                    {fmtEUR(it.prijs * it.aantal)}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Notitie */}
          {order.notitie && (
            <section>
              <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--ink-soft)] font-bold mb-2 inline-flex items-center gap-1">
                <StickyNote className="w-3 h-3" /> Notitie
              </div>
              <div className="rounded-2xl bg-[#FFF4D6] border-2 border-[var(--brand-yellow)] px-4 py-3 italic text-sm leading-relaxed" data-testid="order-detail-notitie">
                {order.notitie}
              </div>
            </section>
          )}

          {/* Timestamps */}
          <section>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--ink-soft)] font-bold mb-2 inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> Tijdlijn
            </div>
            <div className="space-y-1.5 text-sm">
              <Row label="Aangemaakt" value={dt(order.created_at)} testid="order-detail-created" />
              {order.updated_at && order.updated_at !== order.created_at && (
                <Row label="Laatst gewijzigd" value={dt(order.updated_at)} testid="order-detail-updated" />
              )}
              {order.klaar_at && (
                <Row
                  label="Klaar gemeld"
                  value={dt(order.klaar_at)}
                  icon={<CheckCircle2 className="w-3.5 h-3.5 text-[#0E8F2A]" />}
                  testid="order-detail-klaar"
                />
              )}
              {order.afgegeven_at && (
                <Row label="Afgegeven" value={dt(order.afgegeven_at)} testid="order-detail-afgegeven" />
              )}
            </div>
          </section>

          {/* Refund */}
          {order.refund_id && (
            <section>
              <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--ink-soft)] font-bold mb-2 inline-flex items-center gap-1">
                <Ban className="w-3 h-3" /> Refund
              </div>
              <div className="rounded-2xl bg-[#E8F0FF] border-2 border-[var(--brand-blue)] px-4 py-3 text-sm" data-testid="order-detail-refund">
                <Row label="Status" value={order.refund_status || "—"} />
                <Row label="Refund-ID" value={<span className="font-mono text-xs break-all">{order.refund_id}</span>} />
              </div>
            </section>
          )}
        </div>

        {/* Footer Total */}
        <div className="px-6 py-4 border-t-2 border-[#ecebe6] flex items-baseline justify-between bg-[var(--neutral)]">
          <span className="text-xs uppercase tracking-widest text-[var(--ink-soft)] font-bold">
            Totaal
          </span>
          <span className="font-display text-3xl font-extrabold tabular-nums" data-testid="order-detail-totaal">
            {fmtEUR(order.totaal ?? subtotal)}
          </span>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, icon, testid }) {
  return (
    <div className="flex items-baseline gap-2 text-sm" data-testid={testid}>
      {icon}
      <span className="text-[var(--ink-soft)] font-semibold min-w-[120px]">{label}</span>
      <span className="font-bold tabular-nums">{value}</span>
    </div>
  );
}
